import './layout.scss'
import { RootLevelErrorBoundary } from '@omnia/ui/src/lib/cms/core-components/rootLevelErrorBoundary/RootLevelErrorBoundary'
import { useNotificationObserver } from '@omnia/ui/src/lib/hooks/useNotificationObserver'
import { useScrollToAnchor } from '@omnia/ui/src/lib/hooks/useScrollToAnchor'
import { useScrollToTop } from '@omnia/ui/src/lib/hooks/useScrollToTop'
import { LumosLayout } from '@omnia/ui/src/lib/lumos-plus'
import type { LayoutHandles } from '@omnia/ui/src/lib/lumos-plus/components/layout/types/handles'
import { LumosPlusExtension } from '@omnia/ui/src/lib/lumos-plus/extendability/lumos-plus-extendability-context'
import type { LayoutOverrideProps } from '@omnia/ui/src/lib/routing/Layout'
import { getLogger } from '@omnia/ui/src/lib/services/logger/logger.service'
import { NotificationListComponent } from '@vista-digital/occ-ui/src/lib/components/notification-list/notification-list'
import { ErrorBoundary } from '@vista-digital/react-tools'
import type { ErrorInfo, FC } from 'react'
import { memo, Suspense, useRef } from 'react'
import { Outlet } from 'react-router-dom-v7'
import { Announcement } from '../announcements/components/Announcement'
import { CinemaGroupSelector } from '../cinema-groups/components/CinemaGroupSelector'
import { RequiredCinemaGroupPrompt } from '../cinema-groups/components/RequiredCinemaGroupPrompt'
import { ElementDisplayRules } from '../element-display-rules/components/ElementDisplayRules'
import { Popup } from '../popups/components/Popup'

export const Layout: FC<LayoutOverrideProps> = memo(({ rootLevelErrorBoundaryOverride }) => {
    const logRootLevelError = (error: Error, info: ErrorInfo) => {
        const loggerRoot = getLogger(['RootLevelErrorBoundary', error.name]).value
        loggerRoot.error(error.message, { stack: error.stack, componentStack: info.componentStack })
    }

    const layoutRef = useRef<LayoutHandles>(null)

    useScrollToAnchor()
    useScrollToTop()
    useNotificationObserver()

    const ErrorBoundaryElement = rootLevelErrorBoundaryOverride ?? RootLevelErrorBoundary

    return (
        <ErrorBoundary
            fallback={
                <Suspense>
                    <ErrorBoundaryElement />
                </Suspense>
            }
            onError={logRootLevelError}
        >
            <RequiredCinemaGroupPrompt />

            <Announcement />
            <ElementDisplayRules />

            <LumosPlusExtension navigationItemList={CinemaGroupSelector}>
                <LumosLayout ref={layoutRef} useOccV15Modal>
                    <Outlet context={{ layoutRef }} />
                    <NotificationListComponent />
                </LumosLayout>
            </LumosPlusExtension>

            <Popup />
        </ErrorBoundary>
    )
})
